import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTopicsStore, useCardsStore, useIndexCardsStore } from '@/stores'
import '@/components/IndexCard/IndexCard.css'

interface SidebarProps {
  collapsed?: boolean
}

export default function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { getTopicsSorted } = useTopicsStore()
  const { getInboxCount } = useCardsStore()
  const { getIndexCardCount } = useIndexCardsStore()
  const sortedTopics = getTopicsSorted()
  const inboxCount = getInboxCount()
  const indexCardCount = getIndexCardCount()

  const handleBrandClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname === '/dashboard') {
      navigate('/dashboard', { state: { reset: Date.now() }, replace: true })
    } else {
      navigate('/dashboard')
    }
  }

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
      <div className="sidebar-header">
        <Link to="/dashboard" className="sidebar-logo" onClick={handleBrandClick}>
          <span className="logo-text">Branka</span>
          <span className="sidebar-slogan">Craft your ideas</span>
        </Link>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          <Link
            to="/dashboard"
            className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
            </svg>
            <span>Dashboard</span>
          </Link>

          <Link
            to="/search"
            className={`nav-item ${location.pathname === '/search' ? 'active' : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span>Search</span>
          </Link>

          <Link
            to="/inbox"
            className={`nav-item ${location.pathname === '/inbox' ? 'active' : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            <span>Inbox</span>
            {inboxCount > 0 && (
              <span className="inbox-badge">{inboxCount}</span>
            )}
          </Link>

          <Link
            to="/index"
            className={`nav-item nav-item--index ${location.pathname.startsWith('/index') ? 'active' : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            <span className="topic-id topic-id--index">17/</span>
            <span>Index</span>
            {indexCardCount > 0 && (
              <span className="inbox-badge">{indexCardCount}</span>
            )}
          </Link>
        </div>

        <div className="nav-section">
          <div className="nav-section-header">
            <span>Topics</span>
            <Link to="/dashboard" className="nav-section-action" title="Add topic">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </Link>
          </div>

          {sortedTopics.length === 0 ? (
            <p className="nav-empty">No topics yet</p>
          ) : (
            <ul className="nav-list">
              {sortedTopics.map((topic) => (
                <li key={topic.id}>
                  <Link
                    to={`/topic/${topic.id}`}
                    className={`nav-item ${
                      location.pathname === `/topic/${topic.id}` ? 'active' : ''
                    }`}
                  >
                    <span className="topic-id">{topic.id}/</span>
                    <span className="topic-name">{topic.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </nav>
    </aside>
  )
}
